import React, { useCallback, useState } from 'react';
import { formatTranslation } from 'intl';
import { FileTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import LinkIcon from '@mui/icons-material/Link';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Snackbar } from '@mui/material';
import { useGalleryContext } from '..';
import { identitySelector } from '../selectors';
import { ActionButton } from './actionButton';
import { showGenericErrorToast } from 'services/toaster';

interface ShareLinkButtonProps {
  isMobile: boolean;
}

export const ShareLinkButton: React.FC<ShareLinkButtonProps> = ({
  isMobile,
}) => {
  const { fetchShareableUrl } = useGalleryContext();
  const [file] = useGalleryContext().useMainDisplayedFile(identitySelector);
  const intl = useIntl();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const label = formatTranslation(intl, FileTranslationKeys.galleryShareLinkLabel);

  const handleClick = useCallback(async () => {
    if (!file?._id || !fetchShareableUrl) return;

    setLoading(true);
    try {
      const url = await fetchShareableUrl(file._id);
      await navigator.clipboard.writeText(url);
      setSnackbarOpen(true);
    } catch {
      showGenericErrorToast();
    } finally {
      setLoading(false);
    }
  }, [file, fetchShareableUrl]);

  const handleSnackbarClose = useCallback(
    (_event: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') return;
      setSnackbarOpen(false);
    },
    []
  );

  return (
    <>
      <ActionButton
        isMobile={isMobile}
        onClick={handleClick}
        disabled={!file?._id || !fetchShareableUrl || loading}
        tooltipTitle={label}
        label={label}
        icon={<LinkIcon fontSize='small' />}
      />
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        message={formatTranslation(intl, FileTranslationKeys.galleryShareLinkCopiedSnackbar)}
        ContentProps={{ sx: { flexWrap: 'nowrap' } }}
        action={
          <IconButton
            size='small'
            color='inherit'
            onClick={handleSnackbarClose}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        }
      />
    </>
  );
};
